import { useEffect, useState } from 'react';
import { useAwmsStore } from '@/store';
import {
  useFilterWorkloadData,
  useHandleError,
  useAcademicsWorkloadAllocations,
  useSchoolsFilteredByUser,
} from '@/hooks';
import { SearchFilter } from '@/store/types';
import { FILTERS } from '@/constants';
import { updateFilters } from './utils/updateFilters';

export const useAcademicsData = (
  selectedSchoolId: number,
  selectedDisciplineId: number,
  selectedEmpStatusAbbr: SearchFilter[],
  activeYear: number,
) => {
  const handleError = useHandleError();
  const setSelectedSchoolId = useAwmsStore((state) => state.setSelectedSchoolId);
  const setSelectedEmpStatusAbbr = useAwmsStore((state) => state.setSelectedEmpStatusAbbr);
  const searchFilters = useAwmsStore((state) => state.searchFilters);
  const setSearchFilters = useAwmsStore((state) => state.setSearchFilters);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const {
    data: academicsWorkloadAllocations,
    isFetching: isAcademicsWorkloadAllocationsFetching,
    error: academicsWorkloadAllocationsError,
  } = useAcademicsWorkloadAllocations(selectedSchoolId, activeYear, selectedDisciplineId, searchFilters);

  const {
    data: schoolList,
    isLoading: isSchoolListLoading,
    error: schoolListError,
  } = useSchoolsFilteredByUser(activeYear);
  const { disciplineList, empStatusAbbrList, isFilterWorkloadDataLoading } = useFilterWorkloadData(selectedSchoolId);

  useEffect(() => {
    if (!selectedSchoolId && schoolList) {
      updateFilters(schoolList, selectedSchoolId, activeYear, searchFilters, setSelectedSchoolId, setSearchFilters);
    }
  }, [
    selectedSchoolId,
    schoolList,
    selectedEmpStatusAbbr,
    activeYear,
    setSearchFilters,
    searchFilters,
    setSelectedSchoolId,
  ]);

  useEffect(() => {
    if (selectedEmpStatusAbbr.length === 0 && empStatusAbbrList.length > 0) {
      const defaultCheckedValues = empStatusAbbrList
        .filter((empStatusAbbr) => empStatusAbbr.name !== 'SESS')
        .map((empStatusAbbr) => ({ value: empStatusAbbr.id, label: empStatusAbbr.name }));
      setSelectedEmpStatusAbbr(defaultCheckedValues);
      setSearchFilters({ ...searchFilters, [FILTERS.EMPSTATUSABBR]: defaultCheckedValues });
    }
  }, [empStatusAbbrList]);

  useEffect(() => {
    if (schoolListError || academicsWorkloadAllocationsError) {
      console.error('Error fetching data:', { schoolListError, academicsWorkloadAllocationsError });
    }
    handleError([schoolListError, academicsWorkloadAllocationsError]);
  }, [schoolListError, academicsWorkloadAllocationsError, handleError]);

  // This ensures that the isPageLoading state is correctly set based on selectedSchoolId and activeYear.
  // Due to React's hook rules, hooks must be called unconditionally and in the same order on every render.
  // As a result, we cannot conditionally call useUnitWorkloadList (e.g. if selectedSchoolId > 0),
  // which can lead to the isLoading state being incorrectly set for these hooks.
  // Note: Although isUnitWorkloadLoading always return true when selectedSchoolId is 0,
  // the queries do not actually fetch data because the enabled option is set to false in that case.
  const shouldRefetch = selectedSchoolId > 0 && activeYear > 0;

  useEffect(() => {
    setIsPageLoading(
      isSchoolListLoading || (isFilterWorkloadDataLoading && shouldRefetch) || isAcademicsWorkloadAllocationsFetching,
    );
  }, [isSchoolListLoading, isFilterWorkloadDataLoading, isAcademicsWorkloadAllocationsFetching]);

  return {
    schoolList,
    disciplineList,
    empStatusAbbrList,
    academicsWorkloadAllocations,
    isPageLoading,
  };
};
